import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import { financeAPI, usersAPI } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  AlertCircle,
  CheckCircle,
  Plus,
  Download,
  ExternalLink,
  Filter,
  ArrowUpDown,
  Calendar,
  User,
  Search,
  X
} from "lucide-react";
import { cn } from "../lib/utils";

export default function Finanzas() {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [cxc, setCxc] = useState([]);
  const [filteredCxc, setFilteredCxc] = useState([]);
  const [cxp, setCxp] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  
  // Filter states
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: "",
    client_name: "",
    sales_owner_id: "",
    due_date_from: "",
    due_date_to: "",
    min_balance: ""
  });
  const [sortConfig, setSortConfig] = useState({ field: "due_date", order: "asc" });
  
  // Dialog states
  const [cxpDialogOpen, setCxpDialogOpen] = useState(false);
  const [ledgerDialogOpen, setLedgerDialogOpen] = useState(false);
  const [cxpForm, setCxpForm] = useState({
    payee_type: "proveedor",
    payee_name: "",
    concept: "",
    amount: 0,
    due_date: "",
    notes: ""
  });
  const [ledgerForm, setLedgerForm] = useState({
    entry_type: "egreso",
    category: "combustible",
    amount: 0,
    date: new Date().toISOString().slice(0, 10),
    notes: ""
  });

  const categories = {
    combustible: "Combustible",
    peajes: "Peajes",
    mantenimiento: "Mantenimiento",
    seguros: "Seguros",
    nomina: "Nómina",
    comisiones: "Comisiones",
    otros: "Otros"
  };

  useEffect(() => {
    fetchData();
    if (isAdmin) {
      fetchUsers();
    }
  }, []);

  // Apply filters when filters or cxc change
  useEffect(() => {
    applyFilters();
  }, [filters, cxc, sortConfig]);

  const fetchData = async () => {
    try {
      const [cxcRes, cxpRes, ledgerRes] = await Promise.all([
        financeAPI.getCxC(),
        financeAPI.getCxP(),
        financeAPI.getLedger()
      ]);
      setCxc(cxcRes.data);
      setCxp(cxpRes.data);
      setLedger(ledgerRes.data);
    } catch (error) {
      toast.error("Error al cargar datos financieros");
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await usersAPI.getAll();
      setUsers(response.data.filter(u => u.role === "vendedor" || u.role === "admin"));
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const applyFilters = () => {
    let result = [...cxc];
    
    // Apply status filter (ignore "all" value)
    if (filters.status && filters.status !== "all") {
      if (filters.status === "vencido") {
        const today = new Date().toISOString().slice(0, 10);
        result = result.filter(a => a.due_date < today && a.balance > 0);
      } else {
        result = result.filter(a => a.status === filters.status);
      }
    }
    
    // Apply client name filter
    if (filters.client_name) {
      const search = filters.client_name.toLowerCase();
      result = result.filter(a => a.client_name?.toLowerCase().includes(search));
    }
    
    // Apply sales owner filter (ignore "all" value)
    if (filters.sales_owner_id && filters.sales_owner_id !== "all") {
      result = result.filter(a => a.sales_owner_id === filters.sales_owner_id);
    }
    
    // Apply due date filters
    if (filters.due_date_from) {
      result = result.filter(a => a.due_date >= filters.due_date_from);
    }
    if (filters.due_date_to) {
      result = result.filter(a => a.due_date <= filters.due_date_to);
    }
    
    // Apply minimum balance filter
    if (filters.min_balance) {
      const minBal = parseFloat(filters.min_balance);
      result = result.filter(a => a.balance >= minBal);
    }
    
    // Apply sorting
    result.sort((a, b) => {
      let aVal = a[sortConfig.field];
      let bVal = b[sortConfig.field];
      
      if (sortConfig.field === "balance" || sortConfig.field === "total_due") {
        aVal = aVal || 0;
        bVal = bVal || 0;
        return sortConfig.order === "asc" ? aVal - bVal : bVal - aVal;
      }
      
      if (!aVal) return 1;
      if (!bVal) return -1;
      
      const comparison = String(aVal).localeCompare(String(bVal));
      return sortConfig.order === "asc" ? comparison : -comparison;
    });
    
    setFilteredCxc(result);
  };

  const handleSort = (field) => {
    setSortConfig(prev => ({
      field,
      order: prev.field === field && prev.order === "asc" ? "desc" : "asc"
    }));
  };

  const clearFilters = () => {
    setFilters({
      status: "",
      client_name: "",
      sales_owner_id: "",
      due_date_from: "",
      due_date_to: "",
      min_balance: ""
    });
  };

  const getStatusBadge = (account) => {
    const today = new Date().toISOString().slice(0, 10);
    const isOverdue = account.due_date < today && account.balance > 0;
    
    if (isOverdue) {
      return (
        <Badge className="bg-red-900 text-white">
          Vencido
        </Badge>
      );
    }
    
    if (account.status === "pagado") {
      return <Badge className="bg-green-100 text-green-800">Pagado</Badge>;
    }
    if (account.status === "parcial") {
      return <Badge className="bg-yellow-100 text-yellow-800">Parcial</Badge>;
    }
    return <Badge className="bg-red-100 text-red-800">Pendiente</Badge>;
  };

  const totalCxC = cxc.filter(c => c.balance > 0).reduce((sum, c) => sum + c.balance, 0);
  const totalCxP = cxp.filter(c => c.status === "pendiente").reduce((sum, c) => sum + c.amount, 0);
  const totalIngresos = ledger.filter(l => l.entry_type === "ingreso").reduce((sum, l) => sum + l.amount, 0);
  const totalEgresos = ledger.filter(l => l.entry_type === "egreso").reduce((sum, l) => sum + l.amount, 0);
  
  // Count overdue
  const today = new Date().toISOString().slice(0, 10);
  const overdueCount = cxc.filter(c => c.due_date < today && c.balance > 0).length;

  const handleCreateCxP = async () => {
    try {
      await financeAPI.createCxP(cxpForm);
      toast.success("Cuenta por pagar creada");
      setCxpDialogOpen(false);
      setCxpForm({ payee_type: "proveedor", payee_name: "", concept: "", amount: 0, due_date: "", notes: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear cuenta");
    }
  };

  const handlePayCxP = async (id) => {
    try {
      await financeAPI.payCxP(id);
      toast.success("Cuenta pagada");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al pagar cuenta");
    }
  };

  const handleCreateLedger = async () => {
    try {
      await financeAPI.createLedgerEntry(ledgerForm);
      toast.success("Registro creado");
      setLedgerDialogOpen(false);
      setLedgerForm({ entry_type: "egreso", category: "combustible", amount: 0, date: new Date().toISOString().slice(0, 10), notes: "" });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear registro");
    }
  };

  const navigateToContract = (contractId) => {
    navigate(`/contratos/${contractId}`);
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-heading font-bold text-slate-900">Finanzas</h1>
            <p className="text-slate-500 mt-1">Control de cobranza y pagos</p>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="card-hover">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Por Cobrar</p>
                  <p className="text-2xl font-bold text-red-600">${totalCxC.toLocaleString("es-MX")}</p>
                  {overdueCount > 0 && (
                    <p className="text-xs text-red-800 mt-1 flex items-center gap-1">
                      <AlertCircle className="w-3 h-3" />
                      {overdueCount} vencido{overdueCount > 1 ? "s" : ""}
                    </p>
                  )}
                </div>
                <TrendingDown className="w-8 h-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="card-hover">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Por Pagar</p>
                  <p className="text-2xl font-bold text-amber-600">${totalCxP.toLocaleString("es-MX")}</p>
                </div>
                <AlertCircle className="w-8 h-8 text-amber-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="card-hover">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Ingresos</p>
                  <p className="text-2xl font-bold text-green-600">${totalIngresos.toLocaleString("es-MX")}</p>
                </div>
                <TrendingUp className="w-8 h-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="card-hover">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Egresos</p>
                  <p className="text-2xl font-bold text-slate-600">${totalEgresos.toLocaleString("es-MX")}</p>
                </div>
                <DollarSign className="w-8 h-8 text-slate-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="cxc" className="space-y-6">
          <TabsList className="bg-slate-100">
            <TabsTrigger value="cxc" data-testid="tab-cxc">Cuentas por Cobrar</TabsTrigger>
            <TabsTrigger value="cxp" data-testid="tab-cxp">Cuentas por Pagar</TabsTrigger>
            <TabsTrigger value="ledger" data-testid="tab-ledger">Ledger</TabsTrigger>
          </TabsList>

          {/* CxC Tab */}
          <TabsContent value="cxc">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-lg font-heading">Cuentas por Cobrar</CardTitle>
                  <p className="text-sm text-slate-500 mt-1">
                    {filteredCxc.length} registro{filteredCxc.length !== 1 ? "s" : ""}
                    {filters.status || filters.client_name || filters.sales_owner_id ? " (filtrado)" : ""}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowFilters(!showFilters)}
                    className={showFilters ? "bg-slate-100" : ""}
                  >
                    <Filter className="w-4 h-4 mr-2" />
                    Filtros
                  </Button>
                </div>
              </CardHeader>

              {/* Filters Panel */}
              {showFilters && (
                <div className="px-6 pb-4 border-b">
                  <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
                    {/* Status Filter */}
                    <div className="space-y-2">
                      <Label className="text-xs">Estado</Label>
                      <Select 
                        value={filters.status} 
                        onValueChange={(v) => setFilters(prev => ({...prev, status: v}))}
                      >
                        <SelectTrigger className="h-9">
                          <SelectValue placeholder="Todos" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Todos</SelectItem>
                          <SelectItem value="pendiente">Pendiente</SelectItem>
                          <SelectItem value="parcial">Parcial</SelectItem>
                          <SelectItem value="pagado">Pagado</SelectItem>
                          <SelectItem value="vencido">Vencido</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Client Name Filter */}
                    <div className="space-y-2">
                      <Label className="text-xs">Cliente</Label>
                      <div className="relative">
                        <Search className="absolute left-2 top-2.5 h-4 w-4 text-slate-400" />
                        <Input
                          placeholder="Buscar..."
                          value={filters.client_name}
                          onChange={(e) => setFilters(prev => ({...prev, client_name: e.target.value}))}
                          className="h-9 pl-8"
                        />
                      </div>
                    </div>

                    {/* Sales Owner Filter (Admin only) */}
                    {isAdmin && (
                      <div className="space-y-2">
                        <Label className="text-xs">Vendedor</Label>
                        <Select 
                          value={filters.sales_owner_id} 
                          onValueChange={(v) => setFilters(prev => ({...prev, sales_owner_id: v}))}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue placeholder="Todos" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">Todos</SelectItem>
                            {users.map(u => (
                              <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    {/* Due Date From */}
                    <div className="space-y-2">
                      <Label className="text-xs">Vencimiento desde</Label>
                      <Input
                        type="date"
                        value={filters.due_date_from}
                        onChange={(e) => setFilters(prev => ({...prev, due_date_from: e.target.value}))}
                        className="h-9"
                      />
                    </div>

                    {/* Due Date To */}
                    <div className="space-y-2">
                      <Label className="text-xs">Vencimiento hasta</Label>
                      <Input
                        type="date"
                        value={filters.due_date_to}
                        onChange={(e) => setFilters(prev => ({...prev, due_date_to: e.target.value}))}
                        className="h-9"
                      />
                    </div>

                    {/* Min Balance */}
                    <div className="space-y-2">
                      <Label className="text-xs">Saldo mínimo</Label>
                      <Input
                        type="number"
                        placeholder="$0"
                        value={filters.min_balance}
                        onChange={(e) => setFilters(prev => ({...prev, min_balance: e.target.value}))}
                        className="h-9"
                      />
                    </div>
                  </div>
                  
                  <div className="flex justify-end mt-4">
                    <Button variant="ghost" size="sm" onClick={clearFilters}>
                      <X className="w-4 h-4 mr-1" />
                      Limpiar filtros
                    </Button>
                  </div>
                </div>
              )}

              <CardContent className="p-0">
                {loading ? (
                  <div className="p-8 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto"></div>
                  </div>
                ) : filteredCxc.length === 0 ? (
                  <div className="p-8 text-center text-slate-500">
                    {cxc.length === 0 ? "No hay cuentas por cobrar" : "No hay resultados con los filtros aplicados"}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead 
                            className="cursor-pointer hover:bg-slate-50"
                            onClick={() => handleSort("client_name")}
                          >
                            <div className="flex items-center gap-1">
                              Cliente
                              <ArrowUpDown className="w-3 h-3 text-slate-400" />
                            </div>
                          </TableHead>
                          <TableHead>Contrato</TableHead>
                          <TableHead>Vendedor</TableHead>
                          <TableHead 
                            className="cursor-pointer hover:bg-slate-50 text-right"
                            onClick={() => handleSort("total_due")}
                          >
                            <div className="flex items-center justify-end gap-1">
                              Total
                              <ArrowUpDown className="w-3 h-3 text-slate-400" />
                            </div>
                          </TableHead>
                          <TableHead className="text-right">Pagado</TableHead>
                          <TableHead 
                            className="cursor-pointer hover:bg-slate-50 text-right"
                            onClick={() => handleSort("balance")}
                          >
                            <div className="flex items-center justify-end gap-1">
                              Saldo
                              <ArrowUpDown className="w-3 h-3 text-slate-400" />
                            </div>
                          </TableHead>
                          <TableHead 
                            className="cursor-pointer hover:bg-slate-50"
                            onClick={() => handleSort("due_date")}
                          >
                            <div className="flex items-center gap-1">
                              Vencimiento
                              <ArrowUpDown className="w-3 h-3 text-slate-400" />
                            </div>
                          </TableHead>
                          <TableHead>Estado</TableHead>
                          <TableHead className="w-10"></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredCxc.map((account) => {
                          const isOverdue = account.due_date < today && account.balance > 0;
                          return (
                            <TableRow 
                              key={account.id}
                              className={cn(
                                isOverdue && "bg-red-50 hover:bg-red-100"
                              )}
                            >
                              <TableCell className="font-medium">{account.client_name}</TableCell>
                              <TableCell>
                                <button
                                  onClick={() => navigateToContract(account.contract_id)}
                                  className="flex items-center gap-1 text-blue-600 hover:text-blue-800 hover:underline font-mono text-sm"
                                  data-testid={`link-contract-${account.contract_id}`}
                                >
                                  {account.contract_folio && account.contract_folio !== "undefined" 
                                    ? account.contract_folio 
                                    : `CT-${account.contract_id?.slice(0, 8).toUpperCase()}`}
                                  <ExternalLink className="w-3 h-3" />
                                </button>
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-1 text-sm text-slate-600">
                                  <User className="w-3 h-3" />
                                  {account.sales_owner_name || "N/A"}
                                </div>
                              </TableCell>
                              <TableCell className="text-right font-medium">
                                ${account.total_due?.toLocaleString("es-MX")}
                                <span className="text-xs text-slate-400 ml-1">{account.currency || "MXN"}</span>
                              </TableCell>
                              <TableCell className="text-right text-green-600">
                                ${account.total_paid?.toLocaleString("es-MX")}
                              </TableCell>
                              <TableCell className={cn(
                                "text-right font-bold",
                                account.balance > 0 ? "text-red-600" : "text-green-600"
                              )}>
                                ${account.balance?.toLocaleString("es-MX")}
                              </TableCell>
                              <TableCell>
                                <div className="flex items-center gap-1">
                                  <Calendar className="w-3 h-3 text-slate-400" />
                                  <span className={isOverdue ? "text-red-700 font-medium" : ""}>
                                    {new Date(account.due_date).toLocaleDateString("es-MX")}
                                  </span>
                                </div>
                              </TableCell>
                              <TableCell>
                                {getStatusBadge(account)}
                              </TableCell>
                              <TableCell>
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => navigateToContract(account.contract_id)}
                                        data-testid={`view-contract-${account.contract_id}`}
                                      >
                                        <ExternalLink className="w-4 h-4" />
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>Ver contrato</TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* CxP Tab */}
          <TabsContent value="cxp">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg font-heading">Cuentas por Pagar</CardTitle>
                {isAdmin && (
                  <Button onClick={() => setCxpDialogOpen(true)} className="gap-2" data-testid="new-cxp-btn">
                    <Plus className="w-4 h-4" />
                    Nueva CxP
                  </Button>
                )}
              </CardHeader>
              <CardContent className="p-0">
                {loading ? (
                  <div className="p-8 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto"></div>
                  </div>
                ) : cxp.length === 0 ? (
                  <div className="p-8 text-center text-slate-500">No hay cuentas por pagar</div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Tipo</TableHead>
                        <TableHead>Beneficiario</TableHead>
                        <TableHead>Concepto</TableHead>
                        <TableHead className="text-right">Monto</TableHead>
                        <TableHead>Vencimiento</TableHead>
                        <TableHead>Estado</TableHead>
                        {isAdmin && <TableHead>Acciones</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {cxp.map((account) => (
                        <TableRow key={account.id}>
                          <TableCell className="capitalize">{account.payee_type}</TableCell>
                          <TableCell className="font-medium">{account.payee_name}</TableCell>
                          <TableCell>{account.concept}</TableCell>
                          <TableCell className="text-right font-medium">${account.amount?.toLocaleString("es-MX")}</TableCell>
                          <TableCell>{new Date(account.due_date).toLocaleDateString("es-MX")}</TableCell>
                          <TableCell>
                            <Badge className={cn(
                              account.status === "pagado" ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"
                            )}>
                              {account.status}
                            </Badge>
                          </TableCell>
                          {isAdmin && (
                            <TableCell>
                              {account.status === "pendiente" && (
                                <Button size="sm" variant="outline" onClick={() => handlePayCxP(account.id)}>
                                  <CheckCircle className="w-4 h-4 mr-1" />
                                  Pagar
                                </Button>
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Ledger Tab */}
          <TabsContent value="ledger">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg font-heading">Libro Contable</CardTitle>
                {isAdmin && (
                  <Button onClick={() => setLedgerDialogOpen(true)} className="gap-2" data-testid="new-ledger-btn">
                    <Plus className="w-4 h-4" />
                    Nuevo Registro
                  </Button>
                )}
              </CardHeader>
              <CardContent className="p-0">
                {loading ? (
                  <div className="p-8 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900 mx-auto"></div>
                  </div>
                ) : ledger.length === 0 ? (
                  <div className="p-8 text-center text-slate-500">No hay registros en el ledger</div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Fecha</TableHead>
                        <TableHead>Tipo</TableHead>
                        <TableHead>Categoría</TableHead>
                        <TableHead className="text-right">Monto</TableHead>
                        <TableHead>Notas</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {ledger.map((entry) => (
                        <TableRow key={entry.id}>
                          <TableCell>{new Date(entry.date).toLocaleDateString("es-MX")}</TableCell>
                          <TableCell>
                            <Badge className={cn(
                              entry.entry_type === "ingreso" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                            )}>
                              {entry.entry_type}
                            </Badge>
                          </TableCell>
                          <TableCell className="capitalize">{categories[entry.category] || entry.category}</TableCell>
                          <TableCell className={cn(
                            "text-right font-medium",
                            entry.entry_type === "ingreso" ? "text-green-600" : "text-red-600"
                          )}>
                            {entry.entry_type === "ingreso" ? "+" : "-"}${entry.amount?.toLocaleString("es-MX")}
                          </TableCell>
                          <TableCell className="text-slate-500 text-sm max-w-xs truncate">{entry.notes}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* CxP Dialog */}
        <Dialog open={cxpDialogOpen} onOpenChange={setCxpDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading">Nueva Cuenta por Pagar</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Tipo de Beneficiario</Label>
                <Select value={cxpForm.payee_type} onValueChange={(v) => setCxpForm({...cxpForm, payee_type: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="proveedor">Proveedor</SelectItem>
                    <SelectItem value="operador">Operador</SelectItem>
                    <SelectItem value="comisionista">Comisionista</SelectItem>
                    <SelectItem value="otro">Otro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Nombre del Beneficiario</Label>
                <Input value={cxpForm.payee_name} onChange={(e) => setCxpForm({...cxpForm, payee_name: e.target.value})} />
              </div>
              <div className="space-y-2">
                <Label>Concepto</Label>
                <Input value={cxpForm.concept} onChange={(e) => setCxpForm({...cxpForm, concept: e.target.value})} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Monto</Label>
                  <Input type="number" step="any" value={cxpForm.amount} onChange={(e) => setCxpForm({...cxpForm, amount: parseFloat(e.target.value)})} />
                </div>
                <div className="space-y-2">
                  <Label>Fecha de Vencimiento</Label>
                  <Input type="date" value={cxpForm.due_date} onChange={(e) => setCxpForm({...cxpForm, due_date: e.target.value})} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Notas</Label>
                <Textarea value={cxpForm.notes} onChange={(e) => setCxpForm({...cxpForm, notes: e.target.value})} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCxpDialogOpen(false)}>Cancelar</Button>
              <Button onClick={handleCreateCxP}>Crear</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Ledger Dialog */}
        <Dialog open={ledgerDialogOpen} onOpenChange={setLedgerDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading">Nuevo Registro Contable</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Tipo</Label>
                <Select value={ledgerForm.entry_type} onValueChange={(v) => setLedgerForm({...ledgerForm, entry_type: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ingreso">Ingreso</SelectItem>
                    <SelectItem value="egreso">Egreso</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Categoría</Label>
                <Select value={ledgerForm.category} onValueChange={(v) => setLedgerForm({...ledgerForm, category: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(categories).map(([k, v]) => (
                      <SelectItem key={k} value={k}>{v}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Monto</Label>
                  <Input type="number" step="any" value={ledgerForm.amount} onChange={(e) => setLedgerForm({...ledgerForm, amount: parseFloat(e.target.value)})} />
                </div>
                <div className="space-y-2">
                  <Label>Fecha</Label>
                  <Input type="date" value={ledgerForm.date} onChange={(e) => setLedgerForm({...ledgerForm, date: e.target.value})} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Notas</Label>
                <Textarea value={ledgerForm.notes} onChange={(e) => setLedgerForm({...ledgerForm, notes: e.target.value})} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setLedgerDialogOpen(false)}>Cancelar</Button>
              <Button onClick={handleCreateLedger}>Crear</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
